import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Stack,
  Table,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconAlertTriangle, IconBrandWhatsapp, IconClock, IconCreditCard, IconEye, IconHistory,IconLink, IconMail } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";

import {
  TableEmptyRow,
  TableErrorRow,
  TablePagination,
  TableSkeletonRows,
} from "@components/Table";

import { formatDateToString } from "@utils/dateValidation";
import { renderLoadingErrorData } from "@utils/renderLoadingErrorData";
import { showErrorNotification, showSuccessNotification } from "@utils/showNotification";

import { DEFAULT_PAGINATION } from "@constants/index";

import { masterclassQKeys } from "@hooks/queries/masterclass/queryKeys";
import { fetchEnrollmentDetails } from "@hooks/queries/masterclass/useEnrollmentActivity";
import { useGetEnrollments } from "@hooks/queries/masterclass/useGetEnrollments";

import { useAuth } from "@contexts/auth";

import { Masterclass } from "@customTypes/masterclass";

import { EnrollmentAction,EnrollmentActivityModal } from "./EnrollmentActivityModal";


interface EnrollmentTableProps {
  masterclassId: string | null;
  masterclass?: Masterclass;
}

export const EnrollmentTable = ({ masterclassId, masterclass }: EnrollmentTableProps) => {
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<{ id: string; action: EnrollmentAction } | null>(null);
  const [copyingId, setCopyingId] = useState<string | null>(null);
  const copyPaymentLink = async (id: string) => {
    setCopyingId(id);
    try {
      const details = await fetchEnrollmentDetails(id);
      queryClient.setQueryData(masterclassQKeys.enrollmentDetails(id), details);
      if (!details.payment.payment_link) {
        showErrorNotification({ message: "enrollment-no-payment-required" });
        return;
      }
      await navigator.clipboard.writeText(details.payment.payment_link);
      showSuccessNotification({ message: "enrollment-payment-link-copied" });
    } catch {
      showErrorNotification({ message: "enrollment-copy-link-error" });
    } finally {
      setCopyingId(null);
    }
  };
  const [page, setPage] = useState(1);
  const pageSize = DEFAULT_PAGINATION.pageSize;

  const { data, isLoading, isError, isSuccess, isRefetching } = useGetEnrollments({
    masterclassId: masterclassId || undefined,
    isEnabled: !!masterclassId
  });

  const total = data?.length || 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(page, pageCount);
  const paginatedEnrollments = useMemo(
    () => data?.slice((currentPage - 1) * pageSize, currentPage * pageSize) || [],
    [data, currentPage, pageSize]
  );

  useEffect(() => { setPage(1); }, [masterclassId]);
  useEffect(() => { setSelection(null); }, [masterclassId]);
  useEffect(() => { setPage(previous => Math.min(previous, pageCount)); }, [pageCount]);

  const rows = useMemo(() => {
    return paginatedEnrollments.map(item => (
      <Table.Tr key={item._id}>
        <Table.Td>
          <Text fz={14} fw={500}>
            {item.first_name} {item.surname}
          </Text>
        </Table.Td>

        <Table.Td>
          <Stack gap={0}>
            <Group gap={5}>
              <IconMail size={14} color="var(--clr-gray-400)" />
              <Text fz={13}>{item.email || "N/A"}</Text>
            </Group>
            <Group gap={5}>
              <IconBrandWhatsapp size={14} color="var(--clr-green-500)" />
              <Text fz={13}>{item.whatsapp_phone_number || item.phone_number}</Text>
            </Group>
          </Stack>
        </Table.Td>

        <Table.Td>
          <Badge color={item.gender === 'male' ? 'blue' : item.gender === 'female' ? 'pink' : 'gray'}>
            {t(item.gender)}
          </Badge>
        </Table.Td>

        <Table.Td>
          <Text fz={14}>
            {item.created_at ? formatDateToString(new Date(item.created_at)) : "-"}
          </Text>
        </Table.Td>

        <Table.Td>
          <Text fz={13}>{item.residence_location || "-"}</Text>
        </Table.Td>

        <Table.Td>
          <Text fz={13}>{item.industry || "-"}</Text>
        </Table.Td>

        <Table.Td>
          <Text fz={13} lineClamp={2} title={item.participation_reason}>
            {item.participation_reason || "-"}
          </Text>
        </Table.Td>

        <Table.Td>
          <Group gap={6} wrap="nowrap">
          <Badge color={item.payment_transaction_details ? "green" : masterclass?.price === 0 ? "gray" : "orange"} variant="light">
            {item.payment_transaction_details ? t("paid") : masterclass?.price === 0 ? t("enrollment-payment-not_required") : t("pending")}
          </Badge>
          {can("resources.view") && !item.payment_transaction_details && item.payment_attention && (
            <Tooltip multiline w={280} withArrow label={
              <Stack gap={3}>
                <Text size="xs">{t(`enrollment-attention-${item.payment_attention.kind}`)}</Text>
                {item.payment_attention.provider && <Text size="xs">{item.payment_attention.provider}</Text>}
                {item.payment_attention.at && <Text size="xs">{new Date(item.payment_attention.at).toLocaleString(i18n.language)}</Text>}
              </Stack>
            }>
              <ActionIcon
                variant="light"
                color={item.payment_attention.kind === "pending" ? "blue" : "orange"}
                size="sm"
                aria-label={t(`enrollment-attention-${item.payment_attention.kind}`)}
                onClick={() => setSelection({ id: item._id, action: "payments" })}>
                {item.payment_attention.kind === "pending" ? <IconClock size={15} /> : <IconAlertTriangle size={15} />}
              </ActionIcon>
            </Tooltip>
          )}
          </Group>
        </Table.Td>
        <Table.Td ta="right">
          {can("resources.view") && <Menu withArrow position="bottom-end" withinPortal>
            <Menu.Target><Button variant="outline" size="compact-sm" loading={copyingId === item._id}>{t("action")}</Button></Menu.Target>
            <Menu.Dropdown>
              <Menu.Item leftSection={<IconEye size={16} />} onClick={() => setSelection({ id: item._id, action: "details" })}>{t("view-enrollment")}</Menu.Item>
              {!item.payment_transaction_details && masterclass?.price !== 0 && <Menu.Item leftSection={<IconLink size={16} />} disabled={!!copyingId} onClick={() => copyPaymentLink(item._id)}>{t("copy-payment-link")}</Menu.Item>}
              <Menu.Item leftSection={<IconCreditCard size={16} />} onClick={() => setSelection({ id: item._id, action: "payments" })}>{t("payment-activity")}</Menu.Item>
              <Menu.Item leftSection={<IconHistory size={16} />} onClick={() => setSelection({ id: item._id, action: "notifications" })}>{t("notification-history")}</Menu.Item>
            </Menu.Dropdown>
          </Menu>}
        </Table.Td>
      </Table.Tr>
    ));
  }, [paginatedEnrollments, i18n.language, t, can, copyingId, masterclass?.price]);

  if (!masterclassId) {
    return (
      <Table withRowBorders mt="md" withTableBorder>
        <Table.Tbody>
          <TableEmptyRow 
            colSpan={9}
            message={t("please-select-masterclass-to-view-enrollments", "Please select a masterclass to view enrollments")} 
          />
        </Table.Tbody>
      </Table>
    );
  }

  return (
    <Stack gap="md" mb="8rem">
    {masterclass && <Stack gap={3}><Text fw={600}>{masterclass.name}</Text><Text size="sm" c="dimmed">{new Date(masterclass.datetime).toLocaleString(i18n.language)} · {masterclass.provider_details?.full_name}</Text></Stack>}
    {selection && <EnrollmentActivityModal key={`${selection.id}-${selection.action}`} {...selection} close={() => setSelection(null)} />}
    <Table.ScrollContainer minWidth={1200}>
    <Table
      withRowBorders
      mt="md"
      mb="xl"
      miw={1200}
      withTableBorder
      highlightOnHover
      highlightOnHoverColor="var(--clr-gray-100)">      <Table.Thead>
        <Table.Tr bg="var(--clr-gray-100)">
          <Table.Th>{t("participant")}</Table.Th>
          <Table.Th>{t("contact")}</Table.Th>
          <Table.Th>{t("gender")}</Table.Th>
          <Table.Th>{t("enrollment-date")}</Table.Th>
          <Table.Th>{t("residence-location")}</Table.Th>
          <Table.Th>{t("industry")}</Table.Th>
          <Table.Th>{t("participation-reason")}</Table.Th>
          <Table.Th>{t("payment-status")}</Table.Th>
          <Table.Th ta="right">{t("action")}</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {renderLoadingErrorData({
          isLoading: isLoading || isRefetching,
          isError,
          isSuccess,
          dataLength: data?.length || 0,
          emptyDataComponent: (
            <TableEmptyRow colSpan={9} message={t("no-enrollments")} />
          ),
          errorComponent: (
            <TableErrorRow colSpan={9} message={t("encountered-error")} />
          ),
          loadingComponent: (
            <TableSkeletonRows
              columns={9}
              skeletonProps={{
                widths: ["lg", "lg", "sm", "md", "md", "md", "lg", "md"],
              }}
            />
          ),
          dataComponent: rows,
        })}
      </Table.Tbody>
    </Table>
    </Table.ScrollContainer>
    {isSuccess && !isError && (
      <TablePagination
        paginationData={{ page: currentPage, pageSize, pageCount, total }}
        paginatedRows={paginatedEnrollments.length}
        onPageChange={setPage}
        isLoading={isLoading || isRefetching}
      />
    )}
    </Stack>
  );
};
